import React, { createContext, useContext, useState, useEffect, useRef } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, useNavigate, useLocation, Navigate, Link } from "react-router-dom";
import axios from "axios";
import { Toaster } from "./components/ui/sonner";
import { toast } from "sonner";

// Pages
import Home from "./pages/Home";
import Quran from "./pages/Quran";
import QuranReader from "./pages/QuranReader";
import Hafalan from "./pages/Hafalan";
import Habits from "./pages/Habits";
import HabitDetail from "./pages/HabitDetail";
import Reminders from "./pages/Reminders";
import Analytics from "./pages/Analytics";
import Vocabulary from "./pages/Vocabulary";
import Bookmarks from "./pages/Bookmarks";
import Quiz from "./pages/Quiz";
import QuizPlay from "./pages/QuizPlay";
import Doa from "./pages/Doa";
import DoaDetail from "./pages/DoaDetail";
import Tasbih from "./pages/Tasbih";
import DhikrHistory from "./pages/DhikrHistory";
import PrayerTimes from "./pages/PrayerTimes";
import Login from "./pages/Login";
import Register from "./pages/Register";
import Settings from "./pages/Settings";
import Profile from "./pages/Profile";
import CharacterSelect from "./pages/CharacterSelect";

// Gamification
import { GamificationProvider } from "./contexts/GamificationContext";
import { SoundsProvider } from "./contexts/SoundsContext";
import { SidebarProvider, useSidebar } from "./contexts/SidebarContext";
import { QuranAudioProvider } from "./contexts/QuranAudioContext";

// Components
import BottomNav from "./components/layout/BottomNav";
import SideNav from "./components/layout/SideNav";
import GlobalQuranAudioPlayer from "./components/quran/GlobalQuranAudioPlayer";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:8000";
const API = `${BACKEND_URL}/api`;

const LanguageContext = createContext(null);

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within LanguageProvider");
  }
  return context;
};

const LanguageProvider = ({ children }) => {
  const [language, setLanguageState] = useState(() => {
    if (typeof window === "undefined") return "id";
    return localStorage.getItem("app-language") || "id";
  });
  const [translation, setTranslationState] = useState(() => {
    if (typeof window === "undefined") return "indonesian";
    const stored = localStorage.getItem("quran-translation");
    if (stored) return stored;
    return language === "en" ? "english" : "indonesian";
  });
  const [arabicFont, setArabicFontState] = useState(() => {
    if (typeof window === "undefined") return "uthmani";
    return localStorage.getItem("arabic-font") || "uthmani";
  });
  const [themePreset, setThemePresetState] = useState(() => {
    if (typeof window === "undefined") return "default";
    return localStorage.getItem("theme-preset") || "default";
  });
  const [accentColor, setAccentColorState] = useState(() => {
    if (typeof window === "undefined") return "teal";
    return localStorage.getItem("accent-color") || "teal";
  });
  const [quranFontSize, setQuranFontSizeState] = useState(() => {
    if (typeof window === "undefined") return "normal";
    return localStorage.getItem("quran-font-size") || "normal";
  });
  const [dashboardPrefs, setDashboardPrefsState] = useState(() => {
    if (typeof window === "undefined") return { showDailyVerse: true, showPrayerTimes: true, showHabits: true, showProgressSharing: true, showQuote: true, showQuickAccess: true };
    try {
      const raw = localStorage.getItem("dashboard-prefs");
      return raw ? JSON.parse(raw) : { showDailyVerse: true, showPrayerTimes: true, showHabits: true, showProgressSharing: true, showQuote: true, showQuickAccess: true };
    } catch {
      return { showDailyVerse: true, showPrayerTimes: true, showHabits: true, showProgressSharing: true, showQuote: true, showQuickAccess: true };
    }
  });
  const [favoriteShortcuts, setFavoriteShortcutsState] = useState(() => {
    if (typeof window === "undefined") return ["/quran", "/hafalan", "/habits"];
    try {
      const raw = localStorage.getItem("favorite-shortcuts");
      return raw ? JSON.parse(raw) : ["/quran", "/hafalan", "/habits"];
    } catch {
      return ["/quran", "/hafalan", "/habits"];
    }
  });

  useEffect(() => {
    if (typeof window !== "undefined") {
      localStorage.setItem("app-language", language);
      document.documentElement.lang = language;
    }
  }, [language]);

  useEffect(() => {
    if (typeof window !== "undefined") {
      localStorage.setItem("quran-translation", translation);
    }
  }, [translation]);

  useEffect(() => {
    if (typeof window !== "undefined") {
      localStorage.setItem("arabic-font", arabicFont);
    }
  }, [arabicFont]);
  
  useEffect(() => {
    if (typeof window !== "undefined") {
      localStorage.setItem("theme-preset", themePreset);
      const cls = ["theme-default", "theme-mecca", "theme-mosque", "theme-calligraphy"];
      document.body.classList.remove(...cls);
      document.body.classList.add(`theme-${themePreset}`);
    }
  }, [themePreset]);
  
  useEffect(() => {
    if (typeof window !== "undefined") {
      localStorage.setItem("accent-color", accentColor);
      const map = {
        teal: "168 76% 32%",
        emerald: "153 60% 40%",
        indigo: "226 70% 55%",
        rose: "346 75% 57%",
        amber: "37 91% 55%",
        violet: "262 70% 50%",
      };
      const hsl = map[accentColor] || map.teal;
      const el = document.documentElement;
      el.style.setProperty("--primary", hsl);
      el.style.setProperty("--ring", hsl);
      el.style.setProperty("--accent", hsl);
      el.style.setProperty("--accent-foreground", "0 0% 100%");
    }
  }, [accentColor]);
  
  useEffect(() => {
    if (typeof window !== "undefined") {
      localStorage.setItem("quran-font-size", quranFontSize);
    }
  }, [quranFontSize]);
  
  useEffect(() => {
    if (typeof window !== "undefined") {
      try {
        localStorage.setItem("dashboard-prefs", JSON.stringify(dashboardPrefs));
      } catch {}
    }
  }, [dashboardPrefs]);
  
  useEffect(() => {
    if (typeof window !== "undefined") {
      try {
        localStorage.setItem("favorite-shortcuts", JSON.stringify(favoriteShortcuts));
      } catch {}
    }
  }, [favoriteShortcuts]);

  const setLanguage = (next) => {
    setLanguageState(next);
  };

  const setTranslation = (next) => {
    setTranslationState(next);
  };

  const setArabicFont = (next) => {
    setArabicFontState(next);
  };
  
  const setThemePreset = (next) => {
    setThemePresetState(next);
  };
  
  const setAccentColor = (next) => {
    setAccentColorState(next);
  };
  
  const setQuranFontSize = (next) => {
    setQuranFontSizeState(next);
  };
  
  const setDashboardPrefs = (next) => {
    setDashboardPrefsState(next);
  };
  
  const setFavoriteShortcuts = (next) => {
    setFavoriteShortcutsState(next);
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, translation, setTranslation, arabicFont, setArabicFont, themePreset, setThemePreset, accentColor, setAccentColor, quranFontSize, setQuranFontSize, dashboardPrefs, setDashboardPrefs, favoriteShortcuts, setFavoriteShortcuts }}>
      {children}
    </LanguageContext.Provider>
  );
};

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

// Axios instance with credentials
export const apiClient = axios.create({
  baseURL: API,
  withCredentials: true,
  timeout: 10000,
});

const OFFLINE_QUEUE_KEY = "quran-offline-queue";
const BOOKMARKS_CACHE_KEY = "quran-bookmarks-cache";

const loadOfflineQueue = () => {
  if (typeof window === "undefined") return [];
  try {
    return JSON.parse(localStorage.getItem(OFFLINE_QUEUE_KEY) || "[]");
  } catch {
    return [];
  }
};

const saveOfflineQueue = (queue) => {
  if (typeof window === "undefined") return;
  localStorage.setItem(OFFLINE_QUEUE_KEY, JSON.stringify(queue));
};

const loadBookmarksCache = () => {
  if (typeof window === "undefined") return [];
  try {
    return JSON.parse(localStorage.getItem(BOOKMARKS_CACHE_KEY) || "[]");
  } catch {
    return [];
  }
};

const saveBookmarksCache = (bookmarks) => {
  if (typeof window === "undefined") return;
  localStorage.setItem(BOOKMARKS_CACHE_KEY, JSON.stringify(bookmarks));
};

export const enqueueOfflineAction = (action) => {
  const queue = loadOfflineQueue();
  queue.push(action);
  saveOfflineQueue(queue);
  return queue;
};

export const updateOfflineQueue = (updater) => {
  const queue = loadOfflineQueue();
  const next = updater(queue);
  saveOfflineQueue(next);
  return next;
};

export const flushOfflineQueue = async () => {
  if (typeof navigator !== "undefined" && !navigator.onLine) return;
  const queue = loadOfflineQueue();
  if (!queue.length) return;

  const remaining = [];
  for (const item of queue) {
    try {
      if (item.type === "last-read") {
        await apiClient.post("/last-read", item.payload);
        continue;
      }
      if (item.type === "memorization-update") {
        const memRes = await apiClient.get("/memorization");
        const existing = memRes.data.find((mem) => mem.surah_number === item.payload.surah_number);
        const target = existing || (await apiClient.post("/memorization", {
          surah_number: item.payload.surah_number,
          surah_name: item.payload.surah_name,
          total_ayahs: item.payload.total_ayahs,
        })).data;
        await apiClient.patch(`/memorization/${target.mem_id}`, {
          memorized_ayahs: item.payload.memorized_ayahs,
        });
        continue;
      }
      if (item.type === "bookmark-create") {
        const res = await apiClient.post("/bookmarks", item.payload);
        const cached = loadBookmarksCache();
        const next = cached.map((bookmark) =>
          item.tempId && bookmark.bookmark_id === item.tempId ? res.data : bookmark
        );
        if (!item.tempId) {
          next.push(res.data);
        }
        saveBookmarksCache(next);
        continue;
      }
      if (item.type === "bookmark-update") {
        const res = await apiClient.patch(`/bookmarks/${item.payload.bookmark_id}`, item.payload.updateData);
        const cached = loadBookmarksCache();
        const next = cached.map((bookmark) =>
          bookmark.bookmark_id === item.payload.bookmark_id ? res.data : bookmark
        );
        saveBookmarksCache(next);
        continue;
      }
      if (item.type === "bookmark-delete") {
        await apiClient.delete(`/bookmarks/${item.payload.bookmark_id}`);
        const cached = loadBookmarksCache();
        const next = cached.filter((bookmark) => bookmark.bookmark_id !== item.payload.bookmark_id);
        saveBookmarksCache(next);
        continue;
      }
    } catch (error) {
      if (!error?.response) {
        remaining.push(item);
      }
    }
  }

  saveOfflineQueue(remaining);
};

// Auth Provider
const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reminders, setReminders] = useState([]);
  const [reminderSettings, setReminderSettings] = useState(null);
  const [sessionToken, setSessionToken] = useState(() => {
    if (typeof window === "undefined") return null;
    return localStorage.getItem("session-token");
  });
  const { language } = useLanguage();
  const remindersRef = useRef([]);
  const reminderSettingsRef = useRef(null);
  const notificationCacheRef = useRef(new Set());
  const lastRefreshRef = useRef(0);
  const authCheckRef = useRef({ inFlight: false, lastAt: 0 });
  const reminderFetchRef = useRef({ inFlight: false, lastAt: 0 });

  // Add auth header to requests
  useEffect(() => {
    if (sessionToken) {
      apiClient.defaults.headers.common["Authorization"] = `Bearer ${sessionToken}`;
    } else {
      delete apiClient.defaults.headers.common["Authorization"];
    }
  }, [sessionToken]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (sessionToken) {
      localStorage.setItem("session-token", sessionToken);
    } else {
      localStorage.removeItem("session-token");
    }
  }, [sessionToken]);

  const checkAuth = async ({ force = false } = {}) => {
    const now = Date.now();
    if (!force) {
      if (authCheckRef.current.inFlight) return user;
      if (user && now - authCheckRef.current.lastAt < 10000) {
        setLoading(false);
        return user;
      }
    }
    authCheckRef.current.inFlight = true;
    authCheckRef.current.lastAt = now;
    try {
      const response = await apiClient.get("/auth/me");
      setUser(response.data);
      return response.data;
    } catch (error) {
      setUser(null);
      return null;
    } finally {
      authCheckRef.current.inFlight = false;
      setLoading(false);
    }
  };

  const loadReminderData = async ({ force = false } = {}) => {
    const now = Date.now();
    if (!force) {
      if (reminderFetchRef.current.inFlight) return;
      if (now - reminderFetchRef.current.lastAt < 10000) return;
    }
    reminderFetchRef.current.inFlight = true;
    reminderFetchRef.current.lastAt = now;
    try {
      const [remindersRes, settingsRes] = await Promise.all([
        apiClient.get("/reminders"),
        apiClient.get("/reminder-settings")
      ]);
      setReminders(remindersRes.data.reminders || []);
      setReminderSettings(settingsRes.data.settings || null);
    } catch (error) {
      console.error("Failed to load reminders:", error);
    } finally {
      reminderFetchRef.current.inFlight = false;
    }
  };

  const shouldSendOnDate = (reminder, now) => {
    if (reminder.frequency === "daily") return true;
    if (reminder.frequency === "specific_days") {
      const dayMap = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];
      const today = dayMap[now.getDay()];
      return Array.isArray(reminder.days) && reminder.days.includes(today);
    }
    if (reminder.frequency === "weekly") {
      if (reminder.created_at) {
        const created = new Date(reminder.created_at);
        return created.getDay() === now.getDay();
      }
      return true;
    }
    return true;
  };

  const isWithinQuietHours = (settings, now) => {
    if (!settings?.quiet_hours_enabled) return false;
    const [startH, startM] = (settings.quiet_hours_start || "22:00").split(":").map(Number);
    const [endH, endM] = (settings.quiet_hours_end || "06:00").split(":").map(Number);
    const start = startH * 60 + startM;
    const end = endH * 60 + endM;
    const current = now.getHours() * 60 + now.getMinutes();
    if (start <= end) {
      return current >= start && current <= end;
    }
    return current >= start || current <= end;
  };

  const showReminderNotification = async (reminder) => {
    if (!("Notification" in window)) return;
    if (Notification.permission !== "granted") return;

    const title = reminder.title || (language === "en" ? "Reminder" : "Pengingat");
    const message = reminder.message || "";
    try {
      new Notification(title, { body: message, tag: `reminder-${reminder.reminder_id}` });
      await apiClient.post("/notifications/log", {
        reminder_id: reminder.reminder_id,
        title,
        message
      });
    } catch (error) {
      console.error("Failed to send notification:", error);
    }
  };

  const loginWithGoogle = () => {
    // Redirect to Google OAuth - will callback to backend then redirect to frontend with session_token
    const redirectUrl = window.location.origin + '/home';
    // Use window.location.href for direct redirect
    window.location.href = `${BACKEND_URL}/api/auth/google?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const processGoogleSession = async (sessionId) => {
    try {
      const response = await apiClient.post("/auth/session", { session_id: sessionId });
      setUser(response.data.user);
      setSessionToken(response.data.session_token);
      return response.data.user;
    } catch (error) {
      console.error("Failed to process session:", error);
      toast.error(language === "en" ? "Authentication failed" : "Autentikasi gagal");
      return null;
    }
  };

  // Handle Google OAuth callback with session_token in URL
  useEffect(() => {
    const handleGoogleCallback = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const sessionToken = urlParams.get('session_token');

      if (sessionToken && !user) {
        setLoading(true);
        // Set the session token and verify user
        setSessionToken(sessionToken);
        apiClient.defaults.headers.common["Authorization"] = `Bearer ${sessionToken}`;

        // Verify session and get user data
        try {
          const response = await apiClient.get("/auth/me");
          setUser(response.data);

          // Clean URL by removing session_token parameter
          window.history.replaceState({}, document.title, window.location.pathname);

          toast.success(language === "en" ? "Successfully logged in with Google!" : "Berhasil login dengan Google!");
        } catch (error) {
          console.error("Failed to verify session:", error);
          setSessionToken(null);
          setUser(null);
          toast.error(language === "en" ? "Authentication failed" : "Autentikasi gagal");
        } finally {
          setLoading(false);
        }
      }
    };

    handleGoogleCallback();
  }, []);

  const loginWithEmail = async (email, password) => {
    try {
      const response = await apiClient.post("/auth/login", { email, password });
      setUser(response.data.user);
      setSessionToken(response.data.session_token);
      toast.success(language === "en" ? "Welcome back!" : "Selamat datang kembali!");
      return response.data.user;
    } catch (error) {
      const msg = error.response?.data?.detail
        || (error.request
          ? (language === "en" ? "Server unreachable" : "Server tidak bisa diakses")
          : (language === "en" ? "Login failed" : "Login gagal"));
      toast.error(msg);
      throw error;
    }
  };

  const register = async (email, password, name) => {
    try {
      const response = await apiClient.post("/auth/register", { email, password, name });
      setUser(response.data.user);
      setSessionToken(response.data.session_token);
      toast.success(language === "en" ? "Account created successfully!" : "Akun berhasil dibuat!");
      return response.data.user;
    } catch (error) {
      const msg = error.response?.data?.detail || (language === "en" ? "Registration failed" : "Pendaftaran gagal");
      toast.error(msg);
      throw error;
    }
  };

  const logout = async () => {
    try {
      await apiClient.post("/auth/logout");
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      setUser(null);
      setSessionToken(null);
    }
  };

  useEffect(() => {
    checkAuth();
  }, []);

  useEffect(() => {
    if (!loading) return;
    const timeoutId = setTimeout(() => {
      setLoading(false);
    }, 15000);
    return () => clearTimeout(timeoutId);
  }, [loading]);

  useEffect(() => {
    if (!user) return;
    if (typeof window === "undefined") return;

    const offsetMinutes = -new Date().getTimezoneOffset();
    if (user.timezone_offset_minutes === offsetMinutes) return;

    const updateTimezone = async () => {
      try {
        await apiClient.patch("/auth/me", { timezone_offset_minutes: offsetMinutes });
        setUser({ ...user, timezone_offset_minutes: offsetMinutes });
      } catch (error) {
        console.error("Failed to update timezone offset:", error);
      }
    };

    updateTimezone();
  }, [user]);

  useEffect(() => {
    remindersRef.current = reminders;
  }, [reminders]);

  useEffect(() => {
    reminderSettingsRef.current = reminderSettings;
  }, [reminderSettings]);

  useEffect(() => {
    if (!user) return;

    loadReminderData();

    const intervalId = setInterval(() => {
      const settings = reminderSettingsRef.current;
      if (settings?.notification_enabled === false) return;
      if (!("Notification" in window)) return;

      const now = new Date();
      if (isWithinQuietHours(settings, now)) return;

      const currentTime = now.toTimeString().slice(0, 5);
      const todayKey = now.toISOString().split("T")[0];

      remindersRef.current
        .filter((reminder) => reminder.enabled)
        .filter((reminder) => reminder.reminder_time === currentTime)
        .filter((reminder) => shouldSendOnDate(reminder, now))
        .forEach((reminder) => {
          const cacheKey = `${reminder.reminder_id}-${todayKey}-${currentTime}`;
          if (notificationCacheRef.current.has(cacheKey)) return;
          notificationCacheRef.current.add(cacheKey);
          showReminderNotification(reminder);
        });

      const nowTs = Date.now();
      if (nowTs - lastRefreshRef.current > 300000) {
        lastRefreshRef.current = nowTs;
        loadReminderData();
      }
    }, 30000);

    return () => clearInterval(intervalId);
  }, [user, language]);

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        loginWithGoogle,
        processGoogleSession,
        loginWithEmail,
        register,
        logout,
        checkAuth,
        setUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

// Auth Callback Component
const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { processGoogleSession, setUser } = useAuth();
  const { language } = useLanguage();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      const hash = location.hash;
      const sessionIdMatch = hash.match(/session_id=([^&]+)/);
      
      if (sessionIdMatch) {
        const sessionId = sessionIdMatch[1];
        const user = await processGoogleSession(sessionId);
        
        if (user) {
          // Clear hash and navigate
          window.history.replaceState(null, "", window.location.pathname);
          navigate("/home", { replace: true, state: { user } });
        } else {
          navigate("/login", { replace: true });
        }
      } else {
        navigate("/login", { replace: true });
      }
    };

    processAuth();
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-center">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
        <p className="text-muted-foreground">{language === "en" ? "Authenticating..." : "Memproses autentikasi..."}</p>
      </div>
    </div>
  );
};

// Protected Route
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
};

// Layout with Navigation
const AppLayout = ({ children }) => {
  const { toggleMobileSidebar } = useSidebar();

  return (
    <div className="min-h-screen bg-background">
      {/* Mobile Header with Premium Menu Button */}
      <header className="md:hidden sticky top-0 z-30 bg-gradient-to-r from-card to-card/95 backdrop-blur-sm border-b border-border px-4 py-3 flex items-center gap-3 shadow-sm">
        <button
          onClick={toggleMobileSidebar}
          className="p-2.5 hover:bg-muted/80 rounded-xl transition-all duration-200 hover:scale-105 active:scale-95 group relative"
          data-testid="mobile-menu-btn"
        >
          <div className="space-y-1.5">
            <span className="block w-6 h-0.5 bg-foreground group-hover:bg-primary transition-colors duration-200 group-hover:w-5"></span>
            <span className="block w-5 h-0.5 bg-foreground group-hover:bg-primary transition-colors duration-200 group-hover:w-6"></span>
            <span className="block w-4 h-0.5 bg-foreground group-hover:bg-primary transition-colors duration-200 group-hover:w-5"></span>
          </div>
        </button>
        <div className="flex-1">
          <h1 className="text-lg font-bold bg-gradient-to-r from-primary to-primary/70 bg-clip-text text-transparent">
            Quran Companion
          </h1>
        </div>
      </header>

      <SideNav />
      <main className="main-content">
        {children}
      </main>
      <BottomNav />
    </div>
  );
};

// App Router
const AppRouter = () => {
  const location = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, [location.pathname]);

  // Check for session_id in URL hash (synchronously during render)
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      
      {/* Protected routes */}
      <Route
        path="/home"
        element={
          <ProtectedRoute>
            <AppLayout>
              <Home />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/vocabulary"
        element={
          <ProtectedRoute>
            <AppLayout>
              <Vocabulary />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/quran"
        element={
          <ProtectedRoute>
            <AppLayout>
              <Quran />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/quran/:surahNumber"
        element={
          <ProtectedRoute>
            <AppLayout>
              <QuranReader />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/hafalan"
        element={
          <ProtectedRoute>
            <AppLayout>
              <Hafalan />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/habits"
        element={
          <ProtectedRoute>
            <AppLayout>
              <Habits />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/habits/:habitId"
        element={
          <ProtectedRoute>
            <AppLayout>
              <HabitDetail />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/reminders"
        element={
          <ProtectedRoute>
            <AppLayout>
              <Reminders />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/analytics"
        element={
          <ProtectedRoute>
            <AppLayout>
              <Analytics />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/bookmarks"
        element={
          <ProtectedRoute>
            <AppLayout>
              <Bookmarks />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/quiz"
        element={
          <ProtectedRoute>
            <AppLayout>
              <Quiz />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/quiz/play"
        element={
          <ProtectedRoute>
            <AppLayout>
              <QuizPlay />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/doa"
        element={
          <ProtectedRoute>
            <AppLayout>
              <Doa />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/doa/:doaId"
        element={
          <ProtectedRoute>
            <AppLayout>
              <DoaDetail />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/doa/tasbih"
        element={
          <ProtectedRoute>
            <AppLayout>
              <Tasbih />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/doa/history"
        element={
          <ProtectedRoute>
            <AppLayout>
              <DhikrHistory />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/prayer-times"
        element={
          <ProtectedRoute>
            <AppLayout>
              <PrayerTimes />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <AppLayout>
              <Settings />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <AppLayout>
              <Profile />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile/edit"
        element={
          <ProtectedRoute>
            <AppLayout>
              <CharacterSelect />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/characters"
        element={
          <ProtectedRoute>
            <AppLayout>
              <CharacterSelect />
            </AppLayout>
          </ProtectedRoute>
        }
      />

      {/* Default redirect */}
      <Route path="/" element={<Navigate to="/home" replace />} />
      <Route path="*" element={<Navigate to="/home" replace />} />
    </Routes>
  );
};

function App() {
  useEffect(() => {
    flushOfflineQueue();
    if (typeof window === "undefined") return undefined;
    const handleOnline = () => {
      flushOfflineQueue();
    };
    window.addEventListener("online", handleOnline);
    return () => window.removeEventListener("online", handleOnline);
  }, []);

  return (
    <BrowserRouter>
      <LanguageProvider>
        <AuthProvider>
          <SidebarProvider>
            <GamificationProvider>
              <SoundsProvider>
                <QuranAudioProvider>
                  <AppRouter />
                  <GlobalQuranAudioPlayer />
                  <Toaster position="top-center" richColors />
                </QuranAudioProvider>
              </SoundsProvider>
            </GamificationProvider>
          </SidebarProvider>
        </AuthProvider>
      </LanguageProvider>
    </BrowserRouter>
  );
}

export default App;
